import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, DollarSign, Palette, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { settingsApi } from '@/services/api';

const DEFAULT_IMAGES = [
  'https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg',
  'https://images.unsplash.com/photo-1527501034545-bbadafbf308b',
  'https://images.unsplash.com/photo-1650615653338-0ec058c99fa1',
];

const ExperienceBadge = ({ value, label }) => (
  <motion.div
    initial={{ scale: 0 }}
    whileInView={{ scale: 1 }}
    viewport={{ once: true }}
    transition={{ delay: 0.5, type: 'spring' }}
    className="absolute -bottom-6 end-0 bg-gold text-charcoal p-6 shadow-xl z-10"
  >
    <span className="text-4xl font-bold">{value}</span>
    <span className="block text-sm uppercase tracking-wider mt-1">{label}</span>
  </motion.div>
);

// Layout: the original three-image collage
const CollageLayout = ({ images }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-4">
        <div className="aspect-[4/5] overflow-hidden">
          <img src={images[0]} alt="Kitchen design" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </div>
        {images[1] && (
          <div className="aspect-square overflow-hidden">
            <img src={images[1]} alt="Craftsmanship" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      {images[2] && (
        <div className="pt-12">
          <div className="aspect-[3/4] overflow-hidden">
            <img src={images[2]} alt="Wood detail" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>
      )}
    </div>
  </>
);

// Layout: auto-playing fade slider with arrows and dots
const SliderLayout = ({ images, isRTL }) => {
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);

  useEffect(() => {
    if (images.length < 2) return undefined;
    const timer = setInterval(next, 4500);
    return () => clearInterval(timer);
  }, [next, images.length, index]);

  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="relative aspect-[4/5] md:aspect-[5/5] overflow-hidden group">
      <AnimatePresence mode="sync">
        <motion.img
          key={index}
          src={images[index]}
          alt={`Kitchen ${index + 1}`}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous image"
            className="absolute top-1/2 -translate-y-1/2 start-4 w-10 h-10 bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-gold hover:text-charcoal transition-all duration-300 z-10"
          >
            <PrevIcon size={20} />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next image"
            className="absolute top-1/2 -translate-y-1/2 end-4 w-10 h-10 bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-gold hover:text-charcoal transition-all duration-300 z-10"
          >
            <NextIcon size={20} />
          </button>
          <div className="absolute bottom-4 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 flex gap-2 z-10">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Go to image ${i + 1}`}
                className={`h-1.5 transition-all duration-300 ${i === index ? 'w-8 bg-gold' : 'w-3 bg-white/50 hover:bg-white'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Layout: one large image with an offset gold frame
const SingleLayout = ({ images }) => (
  <div className="relative">
    <div className="absolute -top-4 -start-4 w-full h-full border-2 border-gold pointer-events-none" />
    <div className="relative aspect-[4/5] overflow-hidden">
      <img src={images[0]} alt="Kitchen design" loading="lazy" decoding="async" className="w-full h-full object-cover" />
    </div>
  </div>
);

// Layout: even 2x2 photo grid
const GridLayout = ({ images }) => (
  <div className="grid grid-cols-2 gap-4">
    {images.slice(0, 4).map((src, i) => (
      <div key={i} className="aspect-square overflow-hidden">
        <img src={src} alt={`Kitchen ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
    ))}
  </div>
);

const AboutSection = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [settings, setSettings] = useState(null);
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    settingsApi.get().then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const s = settings || {};
  const layout = s.about_layout || 'collage';
  const images = (s.about_images || []).filter((u) => u && u.trim());
  const displayImages = images.length > 0 ? images : DEFAULT_IMAGES;

  const showBadge = s.about_show_badge !== false;
  const badgeValue = s.about_badge_value || '10+';
  const badgeLabel = (isRTL ? s.about_badge_label_ar : s.about_badge_label_en)
    || (isRTL ? 'سنوات خبرة' : 'Years Experience');

  const features = [
    {
      icon: DollarSign,
      title: t('about.features.prices.title'),
      desc: t('about.features.prices.description')
    },
    {
      icon: Palette,
      title: t('about.features.design.title'),
      desc: t('about.features.design.description')
    },
    {
      icon: Users,
      title: t('about.features.team.title'),
      desc: t('about.features.team.description')
    },
  ];

  const renderVisual = () => {
    switch (layout) {
      case 'slider':
        return <SliderLayout images={displayImages} isRTL={isRTL} />;
      case 'single':
        return <SingleLayout images={displayImages} />;
      case 'grid':
        return <GridLayout images={displayImages} />;
      case 'collage':
      default:
        return <CollageLayout images={displayImages} />;
    }
  };

  return (
    <section data-testid="about-section" className="section-padding bg-secondary grain">
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Visual area (admin-selected layout) */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {renderVisual()}
            {showBadge && <ExperienceBadge value={badgeValue} label={badgeLabel} />}
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="label-text mb-4 block">{t('about.label')}</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6">
              {t('about.title')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              {t('about.description')}
            </p>

            {/* Features */}
            <div className="space-y-6 mb-10">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="text-gold" size={22} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Link
              to="/about"
              data-testid="about-read-more"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-gold hover:text-charcoal transition-colors duration-300"
            >
              {t('about.readMore')}
              <Arrow size={18} className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
